import React, { useRef, useState, useEffect } from "react";
import { flushSync } from "react-dom";
import { Info, X } from "lucide-react";
import "./AchievementPage.css";
import WellnessStreaks from "./WellnessStreaks";
import WellnessBadges from "./WellnessBadges";
import { useOnboardingUiEnabled } from "@/hooks/useOnboardingUiEnabled";
import { useIsMobileViewport } from "@/hooks/useIsMobileViewport";

const Coins = ({ coins = 0 }) => {
  const milestones = [10, 50, 100, 500, 1000, 5000, 10000];

  const currentGoal =
    milestones.find((goal) => coins <= goal) ||
    milestones[milestones.length - 1];
  const percentage = Number(((coins / currentGoal) * 100).toFixed(1));

  const [showFireworks, setShowFireworks] = useState(false);
  const [fireworkKey, setFireworkKey] = useState(0);

  useEffect(() => {
    if (coins === currentGoal) {
      const newKey = Date.now();
      setFireworkKey(newKey);
      setShowFireworks(true);

      setTimeout(() => setShowFireworks(false), 6000);
    }
  }, [coins, currentGoal]);

  return (
    <div className="mb-4 bg-[#FFF5EB] p-2 sm:p-3 rounded-md">
      <div className="mb-3 text-[16px]">
        {coins === 0 ? (
          <>
            You're <span className="font-bold">one practice</span> away from
            your first 1-2-3 Wellness coin!
          </>
        ) : (
          <>
            You're <span className="font-bold">{percentage}%</span> of the way
            there with
            <span className="font-bold">
              {" "}
              {coins} {coins === 1 ? "coin" : "coins"}!
            </span>
          </>
        )}
      </div>

      <div className="relative w-full h-7 mb-1">
        {!(coins > 0 && percentage < 5) && (
          <div className="absolute left-0 top-0 flex items-center gap-1">
            <img src="../images/coin front.png" className="w-4 h-auto" />
            <span className="text-[16px]">0</span>
          </div>
        )}

        {coins !== currentGoal && (
          <div
            className="absolute top-0 flex items-center gap-1"
            style={{ left: `${percentage}%`, transform: "translateX(-20%)" }}
          >
            {/* Only show the coin image and coins span if not too close to edges */}
            {percentage >= 10 && percentage <= 90 && (
              <>
                <img src="../images/coin front.png" className="w-4 h-auto" />
                <span className="text-[16px]">{coins}</span>
              </>
            )}
          </div>
        )}

        <div className="absolute right-0 top-0 flex items-center gap-1">
          <img src="../images/coin front.png" className="w-4 h-auto" />
          <span className="text-[16px]">{currentGoal}</span>
        </div>
      </div>

      {showFireworks && (
        <img
          key={fireworkKey}
          src={`../images/Fireworks.gif?${fireworkKey}`}
          alt="Fireworks"
          className="absolute inset-0 w-full h-full object-contain z-20 pointer-events-none"
        />
      )}

      <div className="w-full bg-[#EEEEEE] h-3 rounded overflow-hidden">
        <div
          className="bg-[#FFD0A4] h-full rounded"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const AchievementsPopup = ({
  show,
  coins,
  count,
  time,
  badges,
  onClose,
  maxDayStreak,
  maxWeekStreak,
  curDayStreak,
  curWeekStreak,
  goalWeekRef,
  goalDayRef,
  isLoading = false,
  displayName,
  email,
}) => {
  const coinsRef = useRef(null);
  const streaksRef = useRef(null);
  const badgesRef = useRef(null);

  const [animating, setAnimating] = useState({
    coins: true,
    streaks: false,
    badges: false,
  });

  const handleClick = (type) => {
    if (animating[type]) return;

    // Show only one active GIF at a time
    flushSync(() => {
      setAnimating({
        coins: false,
        badges: false,
        streaks: false,
        [type]: true,
      });
    });

    if (type === "coins") {
      coinsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    if (type === "badges") {
      badgesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    if (type === "streaks") {
      streaksRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      setTimeout(() => {
        goalDayRef.current?.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });

        goalWeekRef.current?.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }, 300);
    }
  };

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const countNum = Number(count) || 0;
  const formattedCount = formatNumber(countNum);
  const totalBadges = badges
    ? Object.values(badges).filter((n) => Number(n) > 0).length
    : 0;
  const coinsNum = Number(coins) || 0;
  const curDayStreakNum = Number(curDayStreak) || 0;
  const curWeekStreakNum = Number(curWeekStreak) || 0;
  const maxDayStreakNum = Number(maxDayStreak) || 0;
  const maxWeekStreakNum = Number(maxWeekStreak) || 0;

  const noAchievementsData =
    countNum <= 0 &&
    coinsNum <= 0 &&
    totalBadges <= 0 &&
    curDayStreakNum <= 0 &&
    curWeekStreakNum <= 0 &&
    maxDayStreakNum <= 0 &&
    maxWeekStreakNum <= 0;

  const isTourActive = useOnboardingUiEnabled();
  const isMobileViewport = useIsMobileViewport();

  const nameCandidate = (displayName && displayName) ?? (email && email);

  const firstName =
    typeof nameCandidate === "string" && nameCandidate.length > 0
      ? nameCandidate.split(" ")[0]
      : (email && email) || "";

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-10 sm:ml-[130px] bg-black bg-opacity-50 flex items-center justify-center"
    >
      <div
        className="bg-white rounded shadow-md w-11/12 sm:w-[70%] xl:w-[50%] pb-3 relative"
        data-onboarding-id="achievements-popup"
      >
        {noAchievementsData && isTourActive ? (
            <div>
              <div className="flex flex-row items-center justify-between border-b py-3 px-3 sm:px-8">
                <div className="mr-2 inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-200">
                  Example with demo data
                </div>
                <button
                  onClick={onClose} className="font-bold text-[12px] text-white cursor-pointer" aria-label="Close"
                  data-onboarding-id="achievements-close"
                >
                  <X />
                </button>
              </div>
              <div className="mb-3 px-3 sm:px-8 sm:h-[75vh] h-[65vh]">
                <img
                src={
                  isMobileViewport
                    ? "/images/onboardingDemo/achievements-mobile.png"
                    : "/images/onboardingDemo/achievements-demo.png"
                }
                alt="Achievements example (demo only)"
                className="mt-2 w-full object-contain rounded-xl border border-black/10 bg-white"
                loading="lazy"
                draggable={false}
              />
              </div>
            </div>
        ) : (
          <>
        {/* Loading overlay with no background - just centers the spinner */}
        {isLoading && (
          <div
            className="absolute top-0 left-0 right-0 bottom-0 rounded-xl flex items-center justify-center"
            style={{ zIndex: 20 }}
          >
            <div className="bg-white p-2 sm:p-4 rounded shadow flex flex-col items-center border border-gray-200">
              <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-b-2 border-blue-600 mb-2 sm:mb-4"></div>
              <span className="text-gray-700 font-medium text-sm">
                Updating your achievements...
              </span>
            </div>
          </div>
        )}

        <div className="sticky top-0 z-10 bg-white">
          <div className="flex flex-row items-center justify-between border-b sm:mb-3 mb-2 py-3 px-3 sm:px-8 gap-2">
            <div
              className="flex flex-col gap-1"
              {...(noAchievementsData
                ? { "data-onboarding-no-data": "achievements" }
                : {})}
            >
              {countNum === 0 ? (
                <div className="text-base">Begin your <strong> 1-2-3 Wellness journey</strong> today to earn achievements!</div>
              ) : (
                <div className="sm:text-xl text-base">
                  <span>Congratulations</span>{" "}
                  <span className="font-bold">{firstName}</span>!
                </div>
              )}
              <div className="text-base">
                You practiced wellness <strong id="wellness-count-display">{formattedCount} </strong>
                {countNum === 1 ? "time" : "times"} in the last{" "}
                <strong>{time}</strong>!
              </div>

            </div>

            <button
              onClick={onClose}
              className="font-bold text-[12px] cursor-pointer"
              data-onboarding-id="achievements-close"
            >
              <X></X>
            </button>
          </div>

          {/* Achievement circles - always visible */}
          <div className="flex justify-center gap-2 sm:gap-3 px-3 sm:px-8 mb-2 sm:mb-3 pb-2">
            <div
              onClick={() => handleClick("coins")}
              ref={coinsRef}
              className="flex flex-col items-center justify-center border-b rounded-md py-3 px-3 lg:px-8 gap-1 bg-[#FFF5EB]"
            >
              <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-[#FFD0A4] flex items-center justify-center">
                <img
                  src={
                    animating.coins
                      ? "../images/testGif.gif"
                      : "../images/coin front.png"
                  }
                  className={
                    animating.coins
                      ? "w-auto h-16 sm:h-24"
                      : "w-12 h-auto sm:w-20 sm:h-auto"
                  }
                />
              </div>
              <p
                className="text-sm sm:text-[24px] font-bold"
                id="wellness-coins-display"
              >
                {coins}
              </p>
              <p className="text-xs sm:text-[16px]">Coins</p>
            </div>

            <div
              onClick={() => handleClick("streaks")}
              ref={streaksRef}
              className="flex flex-col items-center justify-center border-b-2 rounded-md py-3 px-3 lg:px-8 gap-1 bg-[#EBF5FF]"
            >
              <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-[#C7E1FC] flex items-center justify-center">
                <img
                  src={
                    animating.streaks
                      ? "../images/StreakGIF.gif"
                      : "../images/streaks/genericStreaks/BlankStreakBluecolor.png"
                  }
                  className={
                    animating.streaks
                      ? "w-16 h-auto sm:w-24 sm:h-auto"
                      : "w-8 h-auto sm:w-12 sm:h-auto"
                  }
                />
              </div>
              <p className="text-sm sm:text-[24px] font-bold">{maxDayStreak}</p>
              <p className="text-xs sm:text-[16px]">Day Streak</p>
            </div>

            <div
              onClick={() => handleClick("badges")}
              ref={badgesRef}
              className="flex flex-col items-center justify-center border-b-2 rounded-md py-3 px-3 lg:px-8 gap-1 bg-[#FFFCE6]"
            >
              <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-[#FFEC88] flex items-center justify-center">
                <img
                  src={
                    animating.badges
                      ? "../images/BadgeGIF.gif"
                      : "../images/badges/color/BlankBadgeTrophyColor.png"
                  }
                  className={
                    animating.badges
                      ? "w-16 h-auto sm:w-24 sm:h-auto"
                      : "w-12 h-auto sm:w-20 sm:h-auto"
                  }
                />
              </div>
              <p className="text-sm sm:text-[24px] font-bold">{totalBadges}</p>
              <p className="text-xs sm:text-[16px]">Badges</p>
            </div>
          </div>
        </div>

        {/* Scrollable content area - always visible */}
        <div className="px-3 sm:px-8 overflow-y-auto sm:h-[50vh] h-[45vh] w-full">
          <div ref={coinsRef} className="mb-2">
            <div className="text-[16px] font-semibold mb-2 flex items-center gap-2 cursor-pointer">
              <h2>Your Coins</h2>
              <Info size={16} />
            </div>
            <Coins coins={coins} />
          </div>

          <div ref={streaksRef} className="mb-2">
            <div className="text-[16px] font-semibold mb-2 flex items-center gap-2 cursor-pointer">
              <h2>Your Streaks</h2>
              <Info size={16} />
            </div>
            <WellnessStreaks
              curDayStreak={curDayStreak}
              curWeekStreak={curWeekStreak}
              maxDayStreak={maxDayStreak}
              maxWeekStreak={maxWeekStreak}
              goalDayRef={goalDayRef}
              goalWeekRef={goalWeekRef}
            />
          </div>

          <div ref={badgesRef} className="mb-2">
            <div className="text-[16px] font-semibold mb-2 flex items-center gap-2 cursor-pointer">
              <h2>Your Badges</h2>
              <Info size={16} />
            </div>
            <WellnessBadges badges={badges} />
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AchievementsPopup;
